import { cn } from "@universe/ui/lib/utils";
import { Repeat } from "lucide-react";
import { useTranslations } from "next-intl";

import CurrencyFlag from "@/components/currency-flag";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import {
  LEDGER_ROW_BOX_CLASSES,
  LEDGER_ROW_DIVIDER_CLASSES,
  LEDGER_ROW_LAYOUT_CLASSES,
  LEDGER_ROW_SHELL_CLASSES,
} from "@/components/ledger/ledger-row-shell";
import { LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT } from "@/constants";
import { safeDateFormat } from "@/utils/safeDateFormat";

interface LedgerEntryItemCombinedProps {
  index: number;
  entry: CombinedFxEntry;
  setSelectedEntry: () => void;
  selectedIndex: string | null;
}

export default function LedgerEntryItemCombined({
  index,
  entry,
  setSelectedEntry,
  selectedIndex,
}: LedgerEntryItemCombinedProps) {
  const tLedgerTransactions = useTranslations("ledger.transaction");

  // Defensive checks for missing entries (e.g., during pagination)
  if (!entry || !entry.creditEntry || !entry.debitEntry) return null;

  const { creditEntry, debitEntry } = entry;

  // Format date and time — use the combined entry's pre-extracted meta,
  // falling back to the entry-level valueDate
  const transactionDate = safeDateFormat(
    entry.resolvedData?.meta?.createTime || entry.valueDate,
    LEDGER_ENTRY_DATE_TIME_SHORT_FORMAT
  );

  // "From" is the debit (negative/spent), "To" is the credit (positive/received)
  const fromCurrency = debitEntry.product;
  const toCurrency = creditEntry.product;

  const title = tLedgerTransactions("exchange", {
    from: fromCurrency,
    to: toCurrency,
  });

  const isSelected = selectedIndex === `${entry.uuid}-${index}`;

  return (
    <div
      className={`transition-colors hover:bg-gradient-to-l hover:from-white hover:to-brand-primary/5 hover:shadow-md transition-all ${LEDGER_ROW_SHELL_CLASSES}`}
    >
      {/* The row opens the entry drawer, so it is a button named by its
          visible title, amounts and date; its width matches the block box it
          replaced (`ledger-entry-item.tsx`). */}
      <button
        type="button"
        onClick={setSelectedEntry}
        aria-haspopup="dialog"
        aria-expanded={isSelected}
        className={cn(
          LEDGER_ROW_BOX_CLASSES,
          "block w-[calc(100%+24px)] text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-boundary",
          isSelected && "bg-brand-primary/10 shadow-md"
        )}
      >
        {/* Below md the row wraps: the info side takes the full width, so the
            title keeps it, and the amounts and date move onto their own line.
            A capped title beside the amounts kept only ~5 characters at 320px.
            No viewport cap holds the title at any width: at 1024px one cut it
            while the row still had room. `contain:inline-size` keeps the
            nowrap title out of the row's intrinsic width instead. From md up
            it sits on the info side, which then fills the row: without it the
            full title widens the sidebar inset past the screen from lg. Below
            md it sits on the title column: the app's content column clips the
            row there, but a shrink-to-fit ancestor, such as a story's root,
            would still widen.
            The title shrinks to an ellipsis beside the amounts only if every
            box between the info side and the `<p>` has `min-w-0`: from md the
            title column, which sizes to the title, and from xl also the
            wrapper inside it, where the column lays the title and subtitle out
            in one line. Without them the title keeps its full width, so the
            info side's `overflow-hidden` cuts it with no ellipsis, and from xl
            it also pushes the subtitle out of the row. */}
        <div className={cn(LEDGER_ROW_LAYOUT_CLASSES, "items-center")}>
          {/* Left side - Combined basic info */}
          <div className="flex items-center gap-1.5 overflow-hidden min-w-0 basis-full md:flex-1 md:[contain:inline-size]">
            {/* Mobile: Stacked currency flags only (no exchange icon) */}
            <div
              className="relative mr-1.5 md:hidden"
              style={{ width: "36px", height: "36px" }}
            >
              <div className="absolute top-0 left-0">
                <CurrencyFlag size="24px" currency={fromCurrency} />
              </div>
              <div className="absolute bottom-0 right-0">
                <CurrencyFlag size="24px" currency={toCurrency} />
              </div>
            </div>

            {/* Desktop: Exchange icon + stacked flags */}
            <div className="hidden md:contents">
              <Repeat
                strokeWidth={2}
                className="text-primary w-7 h-7 -translate-x-0.5 scale-90"
              />
              <div
                className="relative mr-1.5"
                style={{ width: "36px", height: "36px" }}
              >
                <div className="absolute top-0 left-0">
                  <CurrencyFlag size="24px" currency={fromCurrency} />
                </div>
                <div className="absolute bottom-0 right-0">
                  <CurrencyFlag size="24px" currency={toCurrency} />
                </div>
              </div>
            </div>

            {/* Title and subtitle */}
            <div className="flex flex-col xl:flex-row xl:items-center xl:gap-2 min-w-0 flex-1 [contain:inline-size] md:flex-initial md:[contain:none]">
              <div className="flex items-center min-w-0">
                <p className="font-medium text-base md:text-lg -ml-[1px] xl:ml-0 truncate">
                  {title}
                </p>
              </div>
              <span className="text-gray-800 text-xl xl:text-2xl hidden xl:block">
                •
              </span>
              <span className="text-sm md:text-sm text-gray-700">
                {tLedgerTransactions("fxExchange")}
              </span>
            </div>
          </div>

          {/* Right side - Both amounts and date */}
          <div className="text-right flex items-center gap-10 ml-auto">
            {/* Below md the date wraps under the amounts, and an amount wraps
                before its product code, when long codes leave no room: an
                amount is never truncated. */}
            <div className="text-right flex flex-row flex-wrap justify-end items-center gap-x-3 gap-y-1 whitespace-normal md:flex-col md:flex-nowrap md:items-end md:gap-1 md:whitespace-nowrap xl:flex-row xl:items-center">
              {/* Stacked amounts: positive on top, negative below */}
              <div className="flex flex-col items-end gap-0">
                <FormattedCurrencyAmount
                  balance={creditEntry.balance ?? 0}
                  product={creditEntry.product}
                />
                <FormattedCurrencyAmount
                  balance={debitEntry.balance ?? 0}
                  product={debitEntry.product}
                  className="!text-xs lg:!text-sm !font-normal"
                />
              </div>

              {/* Divider - matches full height of stacked amounts */}
              <div className="mx-6 w-px bg-gray-300 hidden xl:block opacity-50 self-stretch" />

              <p className="text-sm xl:text-base text-gray-700 w-[80px]">
                {transactionDate}
              </p>
            </div>
          </div>
        </div>
      </button>
      <div className={LEDGER_ROW_DIVIDER_CLASSES} />
    </div>
  );
}
